import { useEffect, useRef, useState } from 'react'
import { Map, useMap } from '@vis.gl/react-google-maps'
import { Link } from '@tanstack/react-router'
import { ExternalLink, X } from 'lucide-react'

import { DEFAULT_CENTER } from './grower-area-map'
import { colorForGrower } from './area-map-colors'
import { AreaMapOverlay } from './area-map-overlay'
import type { AreaMapItem } from '@/lib/api/grower-area-map-api'
import {
    GOOGLE_MAPS_MAP_ID,
    GoogleMapsProvider,
    hasGoogleMapsKey,
} from '@/components/maps/google-maps-provider'
import { boundsLiteral } from '@/components/maps/map-controllers'
import { MapPlaceSearch } from '@/components/maps/place-search/map-place-search'
import { Button } from '@/components/ui/button'
import { useAreaMapExtent } from '@/hooks/use-area-map-query'
import { m } from '@/lib/paraglide/messages'

/** Mở lần đầu chưa biết vùng ở đâu: bắt đầu ở mức thấy được vài thửa. */
const INITIAL_ZOOM = 13

/**
 * Bản đồ vùng trồng CHUNG của mọi hộ, chỉ xem. Thửa tải theo ô lưới đang nhìn
 * (lazy-load, cache tới khi reload trang), cùng hộ cùng màu, nhãn "hộ / vùng"
 * đặt tại điểm sâu nhất trong thửa.
 */
export function SharedAreaMap() {
    if (!hasGoogleMapsKey()) {
        return (
            <div className="flex h-full items-center justify-center bg-muted text-sm text-muted-foreground">
                {m.grower_map_no_key()}
            </div>
        )
    }
    return (
        <GoogleMapsProvider>
            <div className="relative z-0 isolate h-full w-full">
                <Map
                    mapId={GOOGLE_MAPS_MAP_ID}
                    defaultCenter={DEFAULT_CENTER}
                    defaultZoom={INITIAL_ZOOM}
                    mapTypeId="hybrid"
                    gestureHandling="greedy"
                    disableDefaultUI={false}
                    streetViewControl={false}
                    style={{ width: '100%', height: '100%' }}
                >
                    <AreaMapContent />
                </Map>
            </div>
        </GoogleMapsProvider>
    )
}

/**
 * Phần bên trong `<Map>`: lớp thửa (tải theo ô, click chọn) + fit lần đầu +
 * thẻ chi tiết của thửa đang chọn.
 */
function AreaMapContent() {
    const [selected, setSelected] = useState<AreaMapItem | null>(null)
    return (
        <>
            <FitToExtent />
            <MapPlaceSearch entry="shared_map" />
            <AreaMapOverlay
                interactive
                initialZoom={INITIAL_ZOOM}
                showBanners
                selectedId={selected?.area_id ?? null}
                onSelect={(a) =>
                    setSelected((cur) =>
                        cur?.area_id === a.area_id ? null : a,
                    )
                }
            />
            {selected && (
                <SelectedCard
                    area={selected}
                    onClose={() => setSelected(null)}
                />
            )}
        </>
    )
}

/** Mở là zoom vào một thửa ở mức thấy rõ nhãn (nhãn hiện từ MIN_LABEL_ZOOM). */
const FOCUS_ZOOM = 15

/**
 * Khung nhìn lần đầu, MỘT LẦN khi extent về, trừ khi người dùng đã kéo bản đồ
 * trước đó (không giật khung nhìn khỏi tay họ):
 *   - có focus → zoom thẳng vào thửa đó (fit cả vùng ra zoom 11 là chỉ thấy
 *     banner "phóng to", khầy Long chốt phải vào thẳng một vùng);
 *   - không focus mà có khung bao → fit khung bao;
 *   - không có thửa nào → giữ DEFAULT_CENTER.
 */
function FitToExtent() {
    const map = useMap()
    const { data: extent } = useAreaMapExtent()
    const doneRef = useRef(false)
    const userRef = useRef(false)

    useEffect(() => {
        if (!map) return
        const l = map.addListener('dragstart', () => {
            userRef.current = true
        })
        return () => l.remove()
    }, [map])

    useEffect(() => {
        if (!map || !extent || doneRef.current || userRef.current) return
        if (extent.count === 0) return
        if (extent.focus_lat != null && extent.focus_lng != null) {
            doneRef.current = true
            map.setCenter({ lat: extent.focus_lat, lng: extent.focus_lng })
            map.setZoom(FOCUS_ZOOM)
            return
        }
        if (
            extent.min_lat == null ||
            extent.max_lat == null ||
            extent.min_lng == null ||
            extent.max_lng == null
        ) {
            return
        }
        doneRef.current = true
        map.fitBounds(
            boundsLiteral([
                { lat: extent.min_lat, lng: extent.min_lng },
                { lat: extent.max_lat, lng: extent.max_lng },
            ]),
            48,
        )
    }, [map, extent])
    return null
}

function SelectedCard({
    area,
    onClose,
}: {
    area: AreaMapItem
    onClose: () => void
}) {
    const color = colorForGrower(area.grower_id)
    return (
        <div
            data-testid="area-selected-card"
            className="absolute bottom-3 left-3 z-10 w-[min(320px,calc(100%-24px))] rounded-lg border bg-background p-3 text-sm shadow-lg"
        >
            <div className="mb-1 flex items-start gap-2">
                <span
                    aria-hidden
                    className="mt-1 size-3 shrink-0 rounded-full"
                    style={{ backgroundColor: color }}
                />
                <div className="min-w-0 flex-1">
                    <div className="truncate font-semibold">
                        {area.grower_name}
                        {area.grower_nickname?.trim()
                            ? ` (${area.grower_nickname.trim()})`
                            : ''}
                    </div>
                    <div className="truncate text-muted-foreground">
                        {area.name} · {area.code}
                    </div>
                </div>
                <button
                    type="button"
                    onClick={onClose}
                    aria-label={m.common_close()}
                    className="rounded p-0.5 text-muted-foreground hover:bg-accent"
                >
                    <X className="size-4" />
                </button>
            </div>
            <div className="mb-2 tabular-nums text-muted-foreground">
                {m.area_map_acreage({
                    acreage: area.acreage.toLocaleString('vi-VN'),
                })}
            </div>
            <Button asChild size="sm" variant="outline" className="w-full">
                <Link
                    to="/agriculture/growers/$id"
                    params={{ id: area.grower_id }}
                >
                    <ExternalLink className="size-3.5" />
                    {m.area_map_open_grower()}
                </Link>
            </Button>
        </div>
    )
}
